// src/components/BrandSlider.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "./LanguageContext";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ========= THEME ========= */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  card: "#fff",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

/* ========= UI ========= */
const Section = styled.section`
  max-width: 1280px;
  margin: 28px auto 40px;
  padding: 0 clamp(10px,4vw,16px);
`;

const Head = styled.div`
  display:flex; align-items:center; justify-content:space-between; gap:12px;
  margin-bottom: 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 900;
  letter-spacing: .2px;
  color: ${COLORS.text};
`;

const Controls = styled.div`
  display:flex; gap:8px;
`;

const IconBtn = styled.button`
  height: 38px; width: 38px; border-radius: 10px;
  border: 1px solid ${COLORS.line}; background:#fff; cursor:pointer;
  display:grid; place-items:center;
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
  &:hover{ transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }
  svg{ width:18px; height:18px; color:${COLORS.text}; }
`;

const Viewport = styled.div`
  position: relative;
  overflow: hidden;
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  background: ${COLORS.bg};
`;

const Track = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  scroll-behavior: smooth;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display:none; }
`;

const Slide = styled(Link)`
  text-decoration: none;
  color: inherit;

  flex: 0 0 16.66%; /* ~6 ბარათი დესკტოპზე */
  min-width: 180px;
  max-width: 220px;

  @media (max-width: 1100px){ flex-basis: 20%; }   /* ~5 */
  @media (max-width: 900px){  flex-basis: 25%; }   /* ~4 */
  @media (max-width: 700px){  flex-basis: 33.33%; }/* ~3 */
  @media (max-width: 520px){  flex-basis: 50%; }   /* ~2 */
`;

const Card = styled.div`
  border: 1px solid ${COLORS.line};
  background: ${COLORS.card};
  border-radius: 14px;
  padding: 16px 14px;
  display: grid;
  place-items: center;
  text-align: center;
  gap: 6px;
  min-height: 82px;
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
  &:hover{ transform: translateY(-2px); box-shadow: ${COLORS.shadow}; border-color: rgba(0,0,0,.12); }
`;

const Name = styled.div`
  min-width: 0;
  display:grid; gap:4px;
  strong{
    font-weight: 900; color: ${COLORS.text}; font-size: .95rem;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    max-width: 100%;
  }
  span{
    color: ${COLORS.dim}; font-size: .85rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    max-width: 100%;
  }
`;

const Empty = styled.div`
  border: 1px dashed ${COLORS.line}; border-radius: 16px;
  padding: 18px; text-align:center; color:${COLORS.dim};
`;

/* ========= helpers ========= */
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
const toSlug = (name) => (name || "").replace(/\s+/g, "-");

/* ========= Component ========= */
export default function BrandSlider({
  titleKa = "ჩვენი ბრენდები",
  titleEn = "Our Brands",
}) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const viewportRef = useRef(null);
  const intervalRef = useRef(null);
  const pauseRef = useRef(false);

  const { language } = useLanguage();
  const selectedTitle = (language && language.startsWith("ka"))
    ? (titleKa || "ჩვენი ბრენდები")
    : (titleEn || "Our Brands");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`${API_BASE_URL}/get_brands.php`);
        if (!mounted) return;
        const picked = shuffle(data || []).slice(0, 10);
        setBrands(picked);
      } catch (e) {
        console.error("❌ Error fetching brands:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // auto-advance
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;

    const step = () => {
      if (pauseRef.current) return;
      const firstCard = vp.querySelector("[data-slide='1']");
      const cardWidth = firstCard ? firstCard.getBoundingClientRect().width + 12 : 240;
      const maxScroll = vp.scrollWidth - vp.clientWidth;
      if (vp.scrollLeft + cardWidth + 4 >= maxScroll) {
        vp.scrollTo({ left: 0, behavior: "auto" });
      } else {
        vp.scrollBy({ left: cardWidth, behavior: "smooth" });
      }
    };

    intervalRef.current = setInterval(step, 2400);
    return () => clearInterval(intervalRef.current);
  }, [brands]);

  const handlePrev = () => {
    const vp = viewportRef.current;
    if (!vp) return;
    const firstCard = vp.querySelector("[data-slide='1']");
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width + 12 : 240;
    vp.scrollBy({ left: -cardWidth, behavior: "smooth" });
  };

  const handleNext = () => {
    const vp = viewportRef.current;
    if (!vp) return;
    const firstCard = vp.querySelector("[data-slide='1']");
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width + 12 : 240;
    vp.scrollBy({ left: cardWidth, behavior: "smooth" });
  };

  const onEnter = () => { pauseRef.current = true; };
  const onLeave = () => { pauseRef.current = false; };

  if (loading) {
    return (
      <Section>
        <Head>
          <Title aria-label={selectedTitle}>{selectedTitle}</Title>
          <Controls>
            <IconBtn onClick={handlePrev} aria-label="Previous"><ChevronLeft /></IconBtn>
            <IconBtn onClick={handleNext} aria-label="Next"><ChevronRight /></IconBtn>
          </Controls>
        </Head>
        <Empty>იტვირთება ბრენდები…</Empty>
      </Section>
    );
  }

  if (!brands.length) {
    return (
      <Section>
        <Head>
          <Title aria-label={selectedTitle}>{selectedTitle}</Title>
          <Controls>
            <IconBtn onClick={handlePrev} aria-label="Previous"><ChevronLeft /></IconBtn>
            <IconBtn onClick={handleNext} aria-label="Next"><ChevronRight /></IconBtn>
          </Controls>
        </Head>
        <Empty>ბრენდები დროებით ხელმისაწვდომი არ არის.</Empty>
      </Section>
    );
  }

  return (
    <Section>
      <Head>
        <Title aria-label={selectedTitle}>{selectedTitle}</Title>
        <Controls>
          <IconBtn onClick={handlePrev} aria-label="Previous"><ChevronLeft /></IconBtn>
          <IconBtn onClick={handleNext} aria-label="Next"><ChevronRight /></IconBtn>
        </Controls>
      </Head>

      <Viewport onMouseEnter={onEnter} onMouseLeave={onLeave} onTouchStart={onEnter} onTouchEnd={onLeave}>
        <Track ref={viewportRef}>
          {brands.map((b, i) => (
            <Slide
              to={`/brands/${toSlug(b.name)}`}
              key={b.id || b.name || i}
              data-slide={i === 0 ? "1" : undefined}
            >
              <Card>
                <Name>
                  <strong title={b.name}>{b.name}</strong>
                  <span>Explore {b.name}</span>
                </Name>
              </Card>
            </Slide>
          ))}
        </Track>
      </Viewport>
    </Section>
  );
}
